import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/lib/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import ChooseRole from "@/pages/ChooseRole";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import ListingEditor from "@/pages/owner/ListingEditor";
import MyBookings from "@/pages/MyBookings";
import AdminPanel from "@/pages/admin/AdminPanel";
import SiteGate from "@/components/SiteGate";

function Protected({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-[60vh] grid place-items-center text-slate-400" data-testid="auth-loading">...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user.needs_role) return <Navigate to="/choose-role" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <SiteGate>
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Search />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/choose-role" element={<ChooseRole />} />
          <Route path="/dashboard" element={<Protected roles={["owner", "agency", "admin"]}><OwnerDashboard /></Protected>} />
          <Route path="/dashboard/listings/new" element={<Protected roles={["owner", "agency", "admin"]}><ListingEditor /></Protected>} />
          <Route path="/dashboard/listings/:id" element={<Protected roles={["owner", "agency", "admin"]}><ListingEditor /></Protected>} />
          <Route path="/my-bookings" element={<Protected><MyBookings /></Protected>} />
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminPanel /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
    </SiteGate>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
